import { v4 as uuid } from 'uuid';

import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { AbstractCommand } from '../AbstractCommand';

import type { VideoEditor } from '../../VideoEditor';

export class AddKaraokeBreakCommand extends AbstractCommand {
  private transcriptTokenId: string;
  private prevClips: KaraokeClip[] | null = null;

  constructor(editor: VideoEditor, transcriptTokenId: string) {
    super(editor);
    this.transcriptTokenId = transcriptTokenId;
  }

  execute(): boolean {
    if (!this.transcriptTokenId) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    // for undo
    this.prevClips = [...track.karaokeClips];

    const newClips: KaraokeClip[] = [];
    for (const clip of track.karaokeClips) {
      const index: number = this.pickClipTokenIndicexToBreakAfter(clip);
      if (index === -1) {
        newClips.push(clip);
      } else {
        newClips.push(...this.splitClipAccordingToBreak(clip, index));
      }
    }
    track.karaokeClips = newClips;

    this.editor.markDirty('karaoke_add_breaks');
    return true;
  }

  private splitClipAccordingToBreak(
    clip: KaraokeClip,
    breakAfterIndex: number,
  ): KaraokeClip[] {
    const splitClips: KaraokeClip[] = [];

    // First part
    const startSegmentTokens = clip.transcriptTokens.slice(
      0,
      breakAfterIndex + 1,
    );
    const startSegmentTokenIds = new Set(startSegmentTokens.map(t => t.id));
    splitClips.push(
      new KaraokeClip({
        id: clip.id,
        transcriptTokens: startSegmentTokens,
        karaokeConfig: clip.karaokeConfig,
        track: clip.track,
        hiddenTranscriptTokenIds: clip.hiddenTranscriptTokenIds.filter(id =>
          startSegmentTokenIds.has(id),
        ),
        ignoredByFiltersIds: clip.ignoredByFiltersIds.filter(id =>
          startSegmentTokenIds.has(id),
        ),
        startSecondsOffset: clip.startSecondsOffset,
        endSecondsOffset: 0,
      }),
    );

    // Second part
    const secondClipStart = breakAfterIndex + 1;
    if (secondClipStart < clip.transcriptTokens.length) {
      const endSegmentTokens = clip.transcriptTokens.slice(secondClipStart);
      const endSegmentTokenIds = new Set(endSegmentTokens.map(t => t.id));
      splitClips.push(
        new KaraokeClip({
          id: uuid(),
          transcriptTokens: clip.transcriptTokens.slice(secondClipStart),
          karaokeConfig: clip.karaokeConfig,
          track: clip.track,
          hiddenTranscriptTokenIds: clip.hiddenTranscriptTokenIds.filter(id =>
            endSegmentTokenIds.has(id),
          ),
          ignoredByFiltersIds: clip.ignoredByFiltersIds.filter(id =>
            endSegmentTokenIds.has(id),
          ),
          startSecondsOffset: 0,
          endSecondsOffset: clip.endSecondsOffset,
        }),
      );
    }

    return splitClips;
  }

  private pickClipTokenIndicexToBreakAfter(clip: KaraokeClip): number {
    for (let i = 0; i < clip.transcriptTokens.length; i++) {
      if (
        this.transcriptTokenId === clip.transcriptTokens[i].id &&
        i < clip.transcriptTokens.length - 1
      ) {
        return i;
      }
    }
    return -1;
  }

  undo(): boolean {
    if (!this.prevClips) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    track.karaokeClips = this.prevClips;

    this.editor.markDirty('karaoke_add_breaks_undo');
    return true;
  }

  getName(): string {
    return 'AddKaraokeBreakCommand';
  }

  getDescription(): string {
    return 'Adds breaks by separating karaoke clips after specified id';
  }
}
